import React, { createContext, useContext, useState, useMemo } from 'react';

function logRenderer(level: 'info' | 'warn' | 'error', message: string, data?: any) {
  const msg = data ? `${message} → ${JSON.stringify(data)}` : message;
  window.logAPI?.sendLog(level, `[AuthContext] ${msg}`);
}

function parseJwt(token: string): Record<string, any> | null {
  try {
    if (!token || typeof token !== 'string') return null;
    logRenderer('info', 'Decoding JWT');
    const parts = token.split('.');
    const base64Url = parts[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);
    return decoded;
  } catch (err) {
    logRenderer('error', 'Failed to decode JWT', err);
    return null;
  }
}

function extractGroups(decoded: Record<string, any> | null): string[] {
  if (!decoded) return [];
  const raw = decoded['cognito:groups'];
  const rawGroups = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  logRenderer('info', 'Extracted groups from token', rawGroups);
  return rawGroups.map((g) => String(g).toLowerCase());
}

function resolveRole(groups: string[], decoded: Record<string, any> | null): string {
  if (groups.includes('admin')) return 'admin';
  if (groups.includes('operator')) return 'operator';
  const custom = decoded?.['custom:role'];
  if (custom != null && custom !== '') return String(custom);
  return 'guest';
}

function resolveUserName(decoded: Record<string, any> | null): string {
  const preferred = decoded?.preferred_username;
  if (preferred != null && String(preferred).trim()) return String(preferred).trim();

  const cognitoUsername = decoded?.['cognito:username'] ?? decoded?.username;
  if (cognitoUsername != null && String(cognitoUsername).trim()) {
    return String(cognitoUsername).trim();
  }

  const email = decoded?.email;
  if (email != null && String(email).trim()) {
    return String(email).trim();
  }

  return 'operator';
}

type AuthContextType = {
  idToken: string | null;
  userEmail: string;
  userName: string;
  role: string;
  exp: number;
  isAdmin: boolean;
  isOperator: boolean;
  setIdToken: (token: string | null) => void;
  hasRole: (...roles: string[]) => boolean;
};

const AuthContext = createContext<AuthContextType>({
  idToken: null,
  userEmail: '',
  userName: '',
  role: 'guest',
  exp: -1,
  isAdmin: false,
  isOperator: false,
  setIdToken: () => {},
  hasRole: () => false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [idToken, setIdTokenState] = useState<string | null>(null);
  const [decoded, setDecoded] = useState<Record<string, any> | null>(null);

  const setIdToken = (token: string | null) => {
    logRenderer('info', `setIdToken called: ${token ? 'Received token' : 'Token cleared'}`);
    setIdTokenState(token);

    if (token) {
      const parsed = parseJwt(token);
      setDecoded(parsed);
    } else {
      setDecoded(null);
    }
  };

  const userEmail = decoded?.email ?? '';
  const userName = resolveUserName(decoded);
  const groups = extractGroups(decoded);
  const role = resolveRole(groups, decoded);
  const exp = decoded?.exp ?? -1;

  const isAdmin = groups.includes('admin');
  const isOperator = groups.includes('operator');

  const hasRole = (...roles: string[]) => {
    logRenderer('info', 'Checking roles', { requested: roles, currentRole: role });
    return roles.includes(role);
  };

  const value = useMemo(() => {
    logRenderer('info', 'Memoized AuthContext updated', {
      idToken: !!idToken,
      email: userEmail,
      userName,
      role,
      exp,
      groups,
      isAdmin,
      isOperator,
    });

    return {
      idToken,
      userEmail,
      userName,
      role,
      isAdmin,
      isOperator,
      exp,
      setIdToken,
      hasRole,
    };
  }, [idToken, userEmail, userName, role, isAdmin, isOperator, exp]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
